import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DiscrepancyBubblesCell } from "components/DiscrepancyBubblesCell";
import { StatusChip } from "components/StatusChip";
import { DISCREPANCY_KIND_NAMES } from "encamp-shared/src/waste/discrepancyKindNames";
import { type MouseEvent, type ReactNode } from "react";

import { DiscrepancyTallyView } from "./sections/discrepancyBreakdown";

export interface DiscrepanciesCellProps {
  /** Open-discrepancy counts split by kind (compliance vs field). */
  tally: DiscrepancyTallyView;
  /** When provided, clicking the cell opens the detail modal (and stops the grid row click). */
  onClick?: () => void;
  /**
   * Customer view: show open EPA correction requests plus the full
   * pending-review / needs-correction / resolved field breakdown. Only the
   * missing-e-Manifest compliance bubble stays hidden, as staff review state
   * with no customer-facing surface. Renders nothing when there's nothing to
   * show. Staff additionally get the compliance bubble.
   */
  customerView?: boolean;
}

/** Tooltip body: a bold header plus a per-line breakdown. */
function BubbleBreakdown({ title, lines }: { title: string; lines: [string, number][] }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        {title}
      </Typography>
      {lines.map(([label, value]) => (
        <Typography key={label} variant="caption" component="div">
          {label}: {value}
        </Typography>
      ))}
    </Box>
  );
}

function EpaCorrectionTooltipBody({
  title,
  countLines,
  previewLines,
}: {
  title: string;
  countLines?: [string, number][];
  previewLines: readonly string[];
}) {
  return (
    <Box>
      {countLines ? (
        <BubbleBreakdown title={title} lines={countLines} />
      ) : (
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
          {title}
        </Typography>
      )}
      {previewLines.map((line) => (
        <Typography
          key={line}
          variant="caption"
          component="div"
          sx={{ mt: 0.5, wordBreak: "break-word" }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
}

function epaCorrectionTooltip(
  epaCorrection: number,
  previewLines: readonly string[],
  staffBreakdown?: {
    missingEManifest: number;
    complianceTotal: number;
  }
): ReactNode {
  if (staffBreakdown) {
    return (
      <EpaCorrectionTooltipBody
        title="EPA-flagged"
        countLines={[
          [DISCREPANCY_KIND_NAMES.EMANIFEST_MISSING.one, staffBreakdown.missingEManifest],
          [DISCREPANCY_KIND_NAMES.EPA_CORRECTION_REQUEST.many, epaCorrection],
          ["Total", staffBreakdown.complianceTotal],
        ]}
        previewLines={previewLines}
      />
    );
  }
  const title = `${epaCorrection} open EPA correction${epaCorrection === 1 ? "" : "s"}`;
  if (previewLines.length === 0) return title;
  return <EpaCorrectionTooltipBody title={title} previewLines={previewLines} />;
}

/**
 * Two discrepancy "bubbles": compliance issues (missing e-Manifest + EPA
 * correction requests, open-only) and field discrepancies (cross-source value
 * disagreements). The field bucket uses the shared {@link DiscrepancyBubblesCell}
 * so it renders the same pending-review / needs-correction / resolved breakdown for
 * everyone, matching the staff review grid — a customer sees the same field
 * counts staff do, because a manifest that reports "no discrepancies" and later
 * sprouts resolved ones reads as data appearing from nowhere. The cell renders
 * nothing when the manifest/line has no discrepancies at all. Shared by the
 * manifest list view and the line-item table.
 */
export function DiscrepanciesCell({ tally, onClick, customerView }: DiscrepanciesCellProps) {
  const {
    complianceTotal,
    missingEManifest,
    epaCorrection,
    epaCorrectionPreviewLines,
    fieldTotal,
    fieldUnresolved,
    fieldPending,
    fieldResolved,
  } = tally;
  const previewLines = epaCorrectionPreviewLines ?? [];

  const clickProps = {
    onClick: onClick
      ? (event: MouseEvent) => {
          event.stopPropagation();
          onClick();
        }
      : undefined,
    sx: onClick ? { cursor: "pointer" } : undefined,
  };

  // The compliance bubble is the one role difference: customers see only the
  // regulator-driven EPA correction requests, never missing-e-Manifest, which
  // has no customer-facing surface anywhere in the flow.
  const compliance = customerView ? epaCorrection : complianceTotal;

  if (compliance === 0 && fieldTotal === 0 && fieldResolved === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" {...clickProps}>
      {compliance > 0 && (
        <Tooltip
          placement="top"
          enterDelay={400}
          title={epaCorrectionTooltip(
            epaCorrection,
            previewLines,
            // A customer's bubble counts EPA corrections alone, so it has no
            // split to show; staff get the missing-e-Manifest breakdown.
            customerView ? undefined : { missingEManifest, complianceTotal }
          )}
        >
          <StatusChip label={compliance} color="error" />
        </Tooltip>
      )}
      <DiscrepancyBubblesCell
        unresolved={fieldUnresolved}
        pending={fieldPending}
        resolved={fieldResolved}
        unresolvedColor="warning"
        pendingColor="info"
      />
    </Stack>
  );
}
